import { CardContent, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { AlarmWarningIcon, SitesIcon } from 'src/assets';
import { useSite } from 'src/contexts/siteContext/sites.context';
import { SiteInterface } from 'src/features/sites/types/site';
import { Address, NewSite } from 'src/features/sites/types/sites';
import { formatSiteType } from 'src/features/sites/utils';
import { SitesService } from 'src/services/sites';
import {
  cardContent,
  cardHeader,
  StyledSiteTypeText,
  cardFooter,
  footerIconBox,
  addressText,
  StyledCard,
  getDirectionsButton,
} from './style';
import { useAuthentication } from 'src/features/authentication/context';
import { SITES_DASHBOARD_PATH } from 'src/routes/config';
import { WarningIcon, InfoIcon } from 'src/features/theme/overrides/CustomIcons';
import { AlarmPriorityType } from 'src/features/alarm-rules/types';
import { GetDirectionsButton } from '../../get-directions-button';

type MobileSiteCardProps = {
  site: NewSite;
  itemRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  index: number;
};

export const MobileSiteCard = ({ site, itemRefs, index }: MobileSiteCardProps) => {
  const theme = useTheme();
  const greyColor = theme.palette.grey[500];
  const addressInstance = new Address(site.location);

  const {
    customerId: { value: customerId },
  } = useAuthentication();
  const navigate = useNavigate();
  const { saveSite } = useSite();

  const handleRedirect = (siteId: string) => {
    if (!customerId) return;
    navigate(SITES_DASHBOARD_PATH + `?customerId=${customerId}&siteId=${siteId}`);
  };

  const handleRowClick = () => {
    if (!customerId) return;
    SitesService.getById({ customerId: customerId, siteId: site.id })
      .then((data: SiteInterface) => {
        saveSite(data);
      })
      .catch((error) => console.error(error));
    handleRedirect(site.id);
  };

  const handleAlarmView = (priority: AlarmPriorityType | undefined) => {
    switch (priority) {
      case 'Critical':
        return <AlarmWarningIcon fill={theme.palette.error.main} />;
      case 'Warning':
        return <WarningIcon htmlColor={theme.palette.warning.main} />;
      case 'Info':
        return <InfoIcon htmlColor={theme.palette.secondary.main} />;
      default:
        return '-';
    }
  };

  return (
    <StyledCard ref={(el) => (itemRefs.current[index] = el)} theme={theme}>
      <CardContent sx={cardContent}>
        <Box sx={cardHeader} onClick={handleRowClick}>
          <Box>
            <Typography data-sm="site-name-row" variant="subtitle1">
              {site.siteName}
            </Typography>
            <StyledSiteTypeText data-sm="site-type-row" theme={theme} variant="body2">
              {formatSiteType(site.siteType)}
            </StyledSiteTypeText>
          </Box>
          <Box data-sm="open-issues-row">{handleAlarmView(site.highestActiveAlarm)}</Box>
        </Box>
        <Box sx={cardFooter}>
          <Box sx={footerIconBox} onClick={handleRowClick}>
            <SitesIcon fill={greyColor} />
          </Box>
          <Box onClick={handleRowClick}>
            <Typography data-sm="address-row" sx={addressText} variant="subtitle2">
              {`${addressInstance}`}
            </Typography>
          </Box>
          <Box sx={getDirectionsButton}>
            <GetDirectionsButton row={site} />
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  );
};
